/**
 * データサービス抽象化レイヤー
 * モックデータサービスとSupabaseサービスの統一インターフェース
 */

import {
  User,
  Role,
  Client,
  LandTransporter,
  Case,
  VehicleModel,
  VehicleType,
  Manufacturer,
  BusinessPartner,
  BusinessPartnerLocation,
  CompanyPartnerSettings,
} from '../types';

// 認証サービスインターフェース
export interface AuthService {
  // モックモード用
  loginWithRole(role: Role): Promise<User>;

  // 本番モード用
  loginWithEmail(email: string, password: string): Promise<User>;
  logout(): Promise<void>;
  getCurrentUser(): Promise<User | null>;

  // ユーザー登録・管理
  signUp?(
    email: string,
    password: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string;
      landTransporterId?: string;
    }
  ): Promise<User>;
  resetPassword?(email: string): Promise<void>;

  // セッション管理
  onAuthStateChange?(callback: (user: User | null) => void): (() => void) | null;
  getSession?(): Promise<any>;
}

// マスターデータサービスインターフェース
export interface MasterDataService {
  // クライアント管理（DEPRECATED: BusinessPartner統合済み）
  createClient(client: Omit<Client, 'id'>): Promise<Client>;
  updateClient(id: string, client: Partial<Client>): Promise<Client>;
  deleteClient(id: string): Promise<void>;

  // 陸送員管理
  getLandTransporters(): Promise<LandTransporter[]>;
  createLandTransporter(transporter: Omit<LandTransporter, 'id'>): Promise<LandTransporter>;
  updateLandTransporter(
    id: string,
    transporter: Partial<LandTransporter>
  ): Promise<LandTransporter>;
  deleteLandTransporter(id: string): Promise<void>;

  // 案件管理
  getCases(): Promise<Case[]>;
  createCase(case_: Omit<Case, 'id'>): Promise<Case>;
  updateCase(id: string, case_: Partial<Case>): Promise<Case>;
  deleteCase(id: string): Promise<void>;

  // 車両モデル管理
  getVehicleModels(): Promise<VehicleModel[]>;
  createVehicleModel(model: Omit<VehicleModel, 'id'>): Promise<VehicleModel>;
  updateVehicleModel(id: string, model: Partial<VehicleModel>): Promise<VehicleModel>;
  deleteVehicleModel(id: string): Promise<void>;

  // メーカー管理
  getManufacturers(): Promise<Manufacturer[]>;
  createManufacturer(manufacturer: Omit<Manufacturer, 'id'>): Promise<Manufacturer>;
  updateManufacturer(id: string, manufacturer: Partial<Manufacturer>): Promise<Manufacturer>;
  deleteManufacturer(id: string): Promise<void>;

  // 車種管理
  getVehicleTypes(): Promise<VehicleType[]>;
  createVehicleType(type: Omit<VehicleType, 'id'>): Promise<VehicleType>;
  updateVehicleType(id: string, type: Partial<VehicleType>): Promise<VehicleType>;
  deleteVehicleType(id: string): Promise<void>;

  // 車両管理（削除済み）

  // FL-28: BusinessPartner管理
  getBusinessPartners(): Promise<BusinessPartner[]>;
  createBusinessPartner(partner: Omit<BusinessPartner, 'id'>): Promise<BusinessPartner>;
  updateBusinessPartner(id: string, partner: Partial<BusinessPartner>): Promise<BusinessPartner>;
  deleteBusinessPartner(id: string): Promise<void>;

  // BusinessPartnerLocation管理
  addBusinessPartnerLocation(
    partnerId: string,
    location: Omit<BusinessPartnerLocation, 'id'>
  ): Promise<BusinessPartner>;
  updateBusinessPartnerLocation(
    partnerId: string,
    locationId: string,
    location: Partial<BusinessPartnerLocation>
  ): Promise<BusinessPartner>;
  deleteBusinessPartnerLocation(partnerId: string, locationId: string): Promise<BusinessPartner>;

  // 後方互換性ヘルパー（DEPRECATED: MasterDataContext統合済み）

  // 会社パートナー設定
  getCompanyPartnerSettings(companyId: string): Promise<CompanyPartnerSettings | null>;
  createCompanyPartnerSettings(
    settings: Omit<CompanyPartnerSettings, 'id'>
  ): Promise<CompanyPartnerSettings>;
  updateCompanyPartnerSettings(
    id: string,
    settings: Partial<CompanyPartnerSettings>
  ): Promise<CompanyPartnerSettings>;

  // データリセット
  resetData(): Promise<void>;
}

// データサービスファクトリー
export interface DataServiceFactory {
  createAuthService(): AuthService;
  createMasterDataService(): MasterDataService;
}

// エラー型定義
export class DataServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'DataServiceError';
  }
}

// 共通エラーコード
export const ErrorCodes = {
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  COMPANY_INACTIVE: 'COMPANY_INACTIVE',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  DATA_ACCESS_ERROR: 'DATA_ACCESS_ERROR',
  DATA_FETCH_FAILED: 'DATA_FETCH_FAILED',
  DATA_CREATION_FAILED: 'DATA_CREATION_FAILED',
  DATA_UPDATE_FAILED: 'DATA_UPDATE_FAILED',
  DATA_DELETION_FAILED: 'DATA_DELETION_FAILED',
  DEPRECATED_METHOD: 'DEPRECATED_METHOD',
  CLIENT_INIT_FAILED: 'CLIENT_INIT_FAILED',
  DATABASE_ERROR: 'DATABASE_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;
